import insightsDashboardImage from "@/assets/insights-dashboard.webp";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { toast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";
import { FeatureItem } from "@/components/education/FeatureItem";
import { EducationPageContainer } from "@/components/education/EducationPageContainer";
import { ENTERPRISE_TRIAL_REQUESTED } from "@/components/tiering/TierViolationDialog";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { ChartLineIcon, HandCoinsIcon, UsersIcon } from "lucide-react";
import { type FC, useCallback } from "react";

export const InsightsEducationPage: FC = () => {
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();

    const { value: trialRequestedValue, setValueAsync: setTrialRequestedValue } =
        usePreferenceValue(ENTERPRISE_TRIAL_REQUESTED);
    const trialRequested = trialRequestedValue === "true";

    const handleRequestTrial = useCallback(async () => {
        try {
            segmentTrack("trial_requested", { type: "enterprise", email: account?.email });
            await setTrialRequestedValue("true");
            toast({
                title: "Trial requested!",
                description: "We'll be in touch about your trial.",
            });
        } catch (error) {
            toast({
                title: "Failed to request trial",
                description: error instanceof Error ? error.message : "An unknown error occurred.",
            });
        }
    }, [account?.email, segmentTrack, setTrialRequestedValue]);

    return (
        <EducationPageContainer>
            <div className="flex flex-col gap-2">
                <Pill variant="brand" size="lg" className="w-fit">
                    Available on Enterprise ✨
                </Pill>
                <Heading1>Turn Insights into actionable intelligence</Heading1>
                <Paragraph className="text-content-secondary text-md">
                    Ona Insights provides powerful analytics to monitor, understand, and optimize your cloud development environment usage, enhancing your team&apos;s productivity and cost-effectiveness.
                </Paragraph>
                
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                    <Button
                        size="md"
                        onClick={handleRequestTrial}
                        disabled={trialRequested}
                        data-tracking-id="request-trial-insights-education-page"
                    >
                        {trialRequested ? "Trial Requested" : "Request trial"}
                    </Button>
                    <Button variant="outline" size="md" asChild>
                        <ExternalLink href="https://ona.com/docs/ona/organizations/insights" iconSize="sm">
                            Learn more
                        </ExternalLink>
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                {/* Image - shows first on mobile, second on desktop */}
                <img
                    src={insightsDashboardImage}
                    alt="Ona Insights dashboard"
                    className="order-1 w-full rounded-lg shadow-xl lg:order-2 lg:w-[65%]"
                />

                {/* Features - shows second on mobile, first on desktop */}
                <div className="order-2 flex flex-col gap-3 lg:order-1 lg:w-[35%]">
                    <Paragraph className="text-base font-medium text-content-primary">Use Insights to:</Paragraph>
                    
                    <FeatureItem
                        icon={<ChartLineIcon size={16} />}
                        variant="brand"
                        title="Analyze usage across your organization"
                        description="Monitor environment usage patterns in real time. See resource consumption for users, projects, and environment classes."
                    />

                    <FeatureItem
                        icon={<HandCoinsIcon size={16} />}
                        variant="success"
                        title="Maximize ROI and manage costs"
                        description="Gain actionable intelligence for optimization for user and project activity and their resource allocation."
                    />

                    <FeatureItem
                        icon={<UsersIcon size={16} />}
                        variant="brand-purple"
                        title="Drive team productivity through insights"
                        description="Unlock maximum productivity by reviewing adoption trends and usage patterns across users and projects."
                    />
                </div>
            </div>
        </EducationPageContainer>
    );
};
